import React from "react";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Textarea from "@cloudscape-design/components/textarea";
import Toggle from "@cloudscape-design/components/toggle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { webhookPresetModalStore, createDefaultWebhookPresetFormData } from "../stores/webhookPresetModal";

const METHOD_OPTIONS = ["POST", "GET", "PUT", "PATCH", "DELETE"].map((method) => ({ value: method, label: method }));
const ALLOWED_PLACEHOLDERS = ["name", "tg", "phone", "email", "uid", "magicLink", "projectMirror"];

function WebhookPresetModal({ Interface }) {
	const modal = useRecoilValue(webhookPresetModalStore);
	const setModal = useSetRecoilState(webhookPresetModalStore);
	const formData = modal.formData;

	const updateField = (field, value) => {
		setModal({
			...modal,
			formData: {
				...formData,
				[field]: value,
			},
		});
	};

	const closeModal = () => {
		setModal({
			...modal,
			show: false,
			mode: "add",
			loaded: true,
			formData: createDefaultWebhookPresetFormData(),
		});
	};

	const getUnknownPlaceholders = (value) => {
		const matches = value.match(/{{\s*[^}]+\s*}}/g) || [];
		return matches
			.map((match) => match.replace(/[{}]/g, "").trim())
			.filter((placeholder) => !ALLOWED_PLACEHOLDERS.includes(placeholder));
	};

	const parseHeaders = () => {
		if (!formData.headersText.trim()) return {};

		try {
			const headers = JSON.parse(formData.headersText);
			if (!headers || typeof headers !== "object" || Array.isArray(headers)) {
				return { error: "Headers JSON must be an object" };
			}

			return { headers };
		} catch (error) {
			return { error: error.message ? `Invalid headers JSON: ${error.message}` : "Invalid headers JSON" };
		}
	};

	const savePreset = async () => {
		if (!formData.name.trim()) {
			Interface.addNoty("Error", "Preset name is required");
			return;
		}

		if (!formData.url.trim()) {
			Interface.addNoty("Error", "URL / path is required");
			return;
		}

		if (!formData.bodyTemplate.trim()) {
			Interface.addNoty("Error", "Body template is required");
			return;
		}

		const unknownPlaceholders = getUnknownPlaceholders(formData.bodyTemplate);
		if (unknownPlaceholders.length) {
			Interface.addNoty("Error", `Unknown template placeholders: ${unknownPlaceholders.join(", ")}`);
			return;
		}

		const parsedHeaders = parseHeaders();
		if (parsedHeaders.error) {
			Interface.addNoty("Error", parsedHeaders.error);
			return;
		}

		const payload = {
			name: formData.name.trim(),
			method: formData.method,
			url: formData.url.trim(),
			headers: parsedHeaders.headers,
			bodyTemplate: formData.bodyTemplate,
			active: formData.active ? 1 : 0,
		};

		if (modal.mode === "edit" && formData.id) {
			payload.id = formData.id;
		}

		setModal({ ...modal, loaded: false });

		try {
			await Interface.request("set", { class: "data", module: "webhookPreset", method: modal.mode === "edit" ? "update" : "create" }, payload);
			Interface.addNoty("Success", `Webhook preset ${modal.mode === "edit" ? "updated" : "created"}`);
			closeModal();
			Interface.loadWebhookPresets();
		} catch (error) {
			Interface.addNoty("Error", error?.text || error?.toString?.() || "Request failed");
			setModal({ ...modal, loaded: true });
		}
	};

	return (
		<Modal
			visible={modal.show}
			onDismiss={closeModal}
			header={modal.mode === "edit" ? "Edit webhook preset" : "Create webhook preset"}
			size="large"
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button variant="normal" onClick={closeModal}>
							Close
						</Button>
						<Button variant="primary" loading={!modal.loaded} onClick={savePreset}>
							Save
						</Button>
					</SpaceBetween>
				</Box>
			}
		>
			<SpaceBetween size="m">
				<FormField label="Name" stretch>
					<Input value={formData.name} placeholder="CRM lead webhook" onChange={({ detail }) => updateField("name", detail.value)} />
				</FormField>
				<FormField label="Method" stretch>
					<Select
						selectedOption={METHOD_OPTIONS.find((option) => option.value === formData.method) || METHOD_OPTIONS[0]}
						options={METHOD_OPTIONS}
						onChange={({ detail }) => updateField("method", detail.selectedOption.value)}
					/>
				</FormField>
				<FormField label="URL / path" description="Absolute URL or backend-supported path where each user webhook request will be sent." stretch>
					<Input value={formData.url} placeholder="https://example.com/webhooks/leads" onChange={({ detail }) => updateField("url", detail.value)} />
				</FormField>
				<FormField label="Headers JSON" stretch>
					<Textarea rows={5} value={formData.headersText} onChange={({ detail }) => updateField("headersText", detail.value)} />
				</FormField>
				<FormField
					label="Body template"
					description="Allowed placeholders: {{name}}, {{tg}}, {{phone}}, {{email}}, {{uid}}, {{magicLink}}, {{projectMirror}}"
					stretch
				>
					<Textarea rows={10} value={formData.bodyTemplate} onChange={({ detail }) => updateField("bodyTemplate", detail.value)} />
				</FormField>
				<Toggle checked={formData.active} onChange={({ detail }) => updateField("active", detail.checked)}>
					Active
				</Toggle>
			</SpaceBetween>
		</Modal>
	);
}

export default WebhookPresetModal;
